import { forwardRef } from 'react';

import { Alert, AlertProps, Button, Link, Typography } from '@mui/material';

import { NotificationCloseButton } from './NotificationCloseButton';

export interface NotificationBannerContentDisplayProps extends Omit<AlertProps, 'action'> {
  link?: string;
  onClick?: () => void;
  onClose?: () => void;
}

export const NotificationBannerContentDisplay = forwardRef<HTMLDivElement, NotificationBannerContentDisplayProps>(
  ({ children, link, onClick, onClose, sx, ...rest }, ref) => {
    return (
      <Alert
        action={onClose ? <NotificationCloseButton onClick={onClose} /> : null}
        severity="info"
        icon={false}
        sx={{
          alignItems: 'center',
          backgroundColor: ({ palette }) => palette.background.iceBlue,
          display: children ? 'flex' : 'none',
          justifyContent: 'center',
          py: 1,
          px: 2,
          '& .MuiAlert-action': {
            m: 0,
            pl: 1,
            alignItems: 'center',
            gap: 2,
          },
          ...sx,
        }}
        ref={ref}
        {...rest}
      >
        {onClick ? (
          <Button
            onClick={onClick}
            sx={{
              all: 'unset',
              '&.MuiButton-root': {
                cursor: 'pointer',
              },
              '&:hover, &:active, &.Mui-focusVisible': {
                all: 'unset',
                textDecoration: 'underline',
                textDecorationColor: ({ palette }) => palette.text.primary,
              },
            }}
          >
            {/* Need to override the default button color */}
            <Typography color={({ palette }) => palette.text.primary}>{children}</Typography>
          </Button>
        ) : link ? (
          <Link
            href={link}
            onClick={onClose}
            color={({ palette }) => palette.text.primary}
            sx={{ '&:visited': { color: ({ palette }) => palette.text.primary } }}
          >
            {children}
          </Link>
        ) : (
          <Typography>{children}</Typography>
        )}
      </Alert>
    );
  }
);
